// frontend\src\pages\ChemicalForm.js
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BiArrowBack, BiSave, BiSolidFlask } from "react-icons/bi";
import api from "../api/api";
import { CHEMICALS_API, CHEMICAL_TYPES_API } from "../constants";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import ScrollToTopButton from "../components/ScrollToTopButton";

const ChemicalForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    chemical_type: "",
    accounting_type: "volume",
    state: "liquid",
    initial_quantity: "",
    quantity: "",
    unit: "ml",
    // Поля для ресурсного учета
    max_uses: 1,
    current_uses: 0,
    manufacturer_recommendation: "",
    // ИСПРАВЛЕНО: перемещены вверх
    barcode: "",
    price: "",
    // Остальные поля
    is_working_solution: false,
    dilution_ratio: "",
    stock_solution: "",
    production_date: "",
    expiry_date: "",
    storage_location: "",
    storage_conditions: "",
    purchase_date: "",
    notes: "",
  });
  const [chemicalTypes, setChemicalTypes] = useState([]);
  const [stockSolutions, setStockSolutions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const urlChemicalType = searchParams.get("chemical_type") || "";
  const isEditing = Boolean(id);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [typesResponse, chemicalsResponse] = await Promise.all([
          api.get(CHEMICAL_TYPES_API),
          api.get(CHEMICALS_API)
        ]);

        setChemicalTypes(typesResponse.data);
        
        const stockSolutionsData = chemicalsResponse.data.filter(
          chem => !chem.is_working_solution
        );
        setStockSolutions(stockSolutionsData);

        if (isEditing) {
          const chemicalResponse = await api.get(`${CHEMICALS_API}${id}/`);
          const chemical = chemicalResponse.data;
          
          const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            return dateString.split("T")[0];
          };

          setFormData({
            name: chemical.name || "",
            brand: chemical.brand || "",
            chemical_type: chemical.chemical_type || "",
            accounting_type: chemical.accounting_type || "volume",
            state: chemical.state || "liquid",
            initial_quantity: chemical.initial_quantity || "",
            quantity: chemical.quantity || "",
            unit: chemical.unit || "ml",
            max_uses: chemical.max_uses || 1,
            current_uses: chemical.current_uses || 0,
            manufacturer_recommendation: chemical.manufacturer_recommendation || "",
            barcode: chemical.barcode || "",
            price: chemical.price || "",
            is_working_solution: chemical.is_working_solution || false,
            dilution_ratio: chemical.dilution_ratio || "",
            stock_solution: chemical.stock_solution || "",
            production_date: formatDateForInput(chemical.production_date),
            expiry_date: formatDateForInput(chemical.expiry_date),
            storage_location: chemical.storage_location || "",
            storage_conditions: chemical.storage_conditions || "",
            purchase_date: formatDateForInput(chemical.purchase_date),
            notes: chemical.notes || "",
          });
        } else {
          // Если это создание нового химиката и есть параметр chemical_type в URL
          if (urlChemicalType) {
            setFormData(prev => ({
              ...prev,
              chemical_type: urlChemicalType
            }));
          }
        }
      } catch (error) {
        toast.error("Ошибка загрузки данных!");
        console.error("Ошибка:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, isEditing, urlChemicalType]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // При смене типа учета сбрасываем соответствующие поля
    if (name === 'accounting_type') {
      if (newValue === 'volume') {
        setFormData(prev => ({
          ...prev,
          max_uses: 1,
          current_uses: 0,
          manufacturer_recommendation: ""
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          initial_quantity: prev.initial_quantity || "",
          quantity: prev.quantity || ""
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        initial_quantity: formData.initial_quantity ? parseFloat(formData.initial_quantity) : 0,
        quantity: formData.quantity ? parseFloat(formData.quantity) : 0,
        max_uses: parseInt(formData.max_uses) || 1,
        current_uses: parseInt(formData.current_uses) || 0,
        manufacturer_recommendation: formData.manufacturer_recommendation ? parseInt(formData.manufacturer_recommendation) : null,
        price: formData.price ? parseFloat(formData.price) : null,
        production_date: formData.production_date || null,
        expiry_date: formData.expiry_date || null,
        purchase_date: formData.purchase_date || null,
        stock_solution: formData.stock_solution || null,
      };

      console.log("Отправляемые данные:", submitData);

      let response;
      if (isEditing) {
        response = await api.put(`${CHEMICALS_API}${id}/`, submitData);
        console.log("Химикат обновлен:", response.data);
        toast.success("Химикат обновлен!");
      } else {
        response = await api.post(CHEMICALS_API, submitData);
        console.log("Химикат создан:", response.data);
        toast.success("Химикат добавлен!");
      }

      navigate("/chemicals", { 
        state: { 
          shouldRefresh: true,
          timestamp: Date.now()
        } 
      });

    } catch (error) {
      console.error("Ошибка сохранения:", error);
      console.error("Детали ошибки:", error.response?.data);
      
      if (error.response?.status === 400) {
        const errors = error.response.data;
        const errorMessages = Object.values(errors).flat().join(", ");
        toast.error(`Ошибка валидации: ${errorMessages}`);
      } else if (error.response?.status === 401) {
        toast.error("Ошибка авторизации");
      } else if (error.response?.status === 500) {
        toast.error("Ошибка сервера");
      } else {
        toast.error("Ошибка сохранения!");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <button
            className="btn btn-outline-secondary me-3"
            onClick={() => navigate("/chemicals")}
          >
            <BiArrowBack />
          </button>
          <h1 className="mb-0" style={{ color: "var(--text-primary)" }}>
            <BiSolidFlask className="me-2" />
            {isEditing ? "Редактирование химиката" : "Добавление химиката"}
          </h1>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Первый ряд - 3 поля */}
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Название продукта *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="brand" className="form-label">
                    Производитель
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="barcode" className="form-label">
                    Штрих-код
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="barcode"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Второй ряд - 3 поля */}
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="chemical_type" className="form-label">
                    Тип химиката *
                  </label>
                  <select
                    className="form-select"
                    id="chemical_type"
                    name="chemical_type"
                    value={formData.chemical_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Выберите тип...</option>
                    {chemicalTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="state" className="form-label">
                    Состояние *
                  </label>
                  <select
                    className="form-select"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  >
                    <option value="liquid">Жидкость</option>
                    <option value="powder">Порошок</option>
                    <option value="concentrate">Концентрат</option>
                  </select>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="price" className="form-label">
                    Цена (руб.)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Тип учета */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="card-title">Тип учета</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <select
                            className="form-select"
                            id="accounting_type"
                            name="accounting_type"
                            value={formData.accounting_type}
                            onChange={handleChange}
                          >
                            <option value="volume">Объемный учет (концентраты, порошки)</option>
                            <option value="resource">Ресурсный учет (рабочие растворы)</option>
                          </select>
                          <div className="form-text">
                            {formData.accounting_type === 'volume' 
                              ? 'Учет по объему - количество уменьшается при каждом использовании'
                              : 'Учет по использованиям - отслеживается количество применений раствора'
                            }
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Поля для ресурсного учета */}
                    {formData.accounting_type === 'resource' && (
                      <div className="row mt-3">
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label htmlFor="max_uses" className="form-label">
                              Максимум использований *
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="max_uses"
                              name="max_uses"
                              value={formData.max_uses}
                              onChange={handleChange}
                              min="1"
                              required
                            />
                            <div className="form-text">
                              Сколько раз можно использовать раствор
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="mb-3">
                            <label htmlFor="current_uses" className="form-label">
                              Текущее использований
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="current_uses"
                              name="current_uses"
                              value={formData.current_uses}
                              onChange={handleChange}
                              min="0"
                            />
                            <div className="form-text">
                              Уже использовано раз
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="mb-3">
                            <label htmlFor="manufacturer_recommendation" className="form-label">
                              Рекомендация производителя
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="manufacturer_recommendation"
                              name="manufacturer_recommendation"
                              value={formData.manufacturer_recommendation}
                              onChange={handleChange}
                              min="1"
                            />
                            <div className="form-text">
                              По инструкции
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Количества */}
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="initial_quantity" className="form-label">
                    Исходное количество *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    id="initial_quantity"
                    name="initial_quantity"
                    value={formData.initial_quantity}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                  <div className="form-text">
                    {formData.accounting_type === 'volume' 
                      ? 'Объем при покупке/приготовлении'
                      : 'Объем рабочего раствора'
                    }
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="quantity" className="form-label">
                    Текущее количество *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                  <div className="form-text">
                    {formData.accounting_type === 'volume' 
                      ? 'Текущий остаток'
                      : 'Текущий объем раствора'
                    }
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="unit" className="form-label">
                    Единица измерения *
                  </label>
                  <select
                    className="form-select"
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    required
                  >
                    <option value="ml">мл</option>
                    <option value="l">л</option>
                    <option value="g">г</option>
                    <option value="kg">кг</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Рабочий раствор */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is_working_solution"
                    name="is_working_solution"
                    checked={formData.is_working_solution}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="is_working_solution">
                    Это рабочий раствор
                  </label>
                </div>
              </div>
            </div>

            {formData.is_working_solution && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="dilution_ratio" className="form-label">
                      Коэффициент разведения
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="dilution_ratio"
                      name="dilution_ratio"
                      value={formData.dilution_ratio}
                      onChange={handleChange}
                      placeholder="Например: 1+9, 1+4 и т.д."
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="stock_solution" className="form-label">
                      Исходный раствор
                    </label>
                    <select
                      className="form-select"
                      id="stock_solution"
                      name="stock_solution"
                      value={formData.stock_solution}
                      onChange={handleChange}
                    >
                      <option value="">Выберите исходный раствор...</option>
                      {stockSolutions.map((chemical) => (
                        <option key={chemical.id} value={chemical.id}>
                          {chemical.brand} {chemical.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Даты */}
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="production_date" className="form-label">
                    Дата {formData.is_working_solution ? 'приготовления' : 'производства'}
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="production_date"
                    name="production_date"
                    value={formData.production_date}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="expiry_date" className="form-label">
                    Срок годности
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="expiry_date"
                    name="expiry_date"
                    value={formData.expiry_date}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="purchase_date" className="form-label">
                    Дата покупки
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="purchase_date"
                    name="purchase_date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Хранение */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="storage_location" className="form-label">
                    Место хранения
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="storage_location"
                    name="storage_location"
                    value={formData.storage_location}
                    onChange={handleChange}
                    placeholder="Например: холодильник, полка в шкафу и т.д."
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="storage_conditions" className="form-label">
                    Условия хранения
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="storage_conditions"
                    name="storage_conditions"
                    value={formData.storage_conditions}
                    onChange={handleChange}
                    placeholder="Например: комнатная температура, в темноте и т.д."
                  />
                </div>
              </div>
            </div>

            {/* Примечания */}
            <div className="mb-3">
              <label htmlFor="notes" className="form-label">
                Примечания
              </label>
              <textarea
                className="form-control"
                id="notes"
                name="notes"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Дополнительная информация о химикате..."
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/chemicals")}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                <BiSave className="me-2" />
                {isSubmitting ? "Сохранение..." : isEditing ? "Обновить" : "Сохранить"}
              </button>
            </div>
          </form>    
        </div>
      </div>
      <ScrollToTopButton/>
    </div>
  );
};

export default ChemicalForm;